import { createRouter, createWebHistory } from "vue-router";
import DashboardLayout from "@/layouts/DashboardLayout.vue";
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import { auth } from "../services/db.js";
import { canUseLit } from "../services/enabled_features.js";

const needsAuth = true;

const routes = [
  {
    path: "/",
    redirect: "/dashboard",
  },
  {
    path: "/signin",
    name: "Sign In",
    component: () =>
      import(/* webpackChunkName: "signin" */ "../views/SignIn.vue"),
  },
  {
    path: "/resetPwd",
    name: "Reset Password",
    component: () =>
      import(/* webpackChunkName: "resetPwd" */ "../views/ResetPwd.vue"),
  },
  {
    path: "/register",
    name: "Register",
    component: () =>
      import(/* webpackChunkName: "register" */ "../views/Register.vue"),
  },
  {
    // WeChat H5 entry: notification links inside WeChat land here via the
    // backend handoff. Public route — it mints its own session from the
    // one-time handoff token.
    path: "/wechat/entry",
    name: "Wechat Entry",
    component: () =>
      import(/* webpackChunkName: "wechat" */ "../views/WechatEntry.vue"),
  },
  {
    // OAuth consent for the Claude MCP connector: the backend's
    // /oauth/authorize redirects here with a one-time handoff token. Public
    // route — the page handles sign-in itself, then approves/denies the grant.
    path: "/oauth/consent",
    name: "OAuth Consent",
    component: () =>
      import(/* webpackChunkName: "connector" */ "../views/OauthConsent.vue"),
  },
  {
    path: "/account",
    name: "Account",
    component: () =>
      import(/* webpackChunkName: "account" */ "../views/AccountInfo.vue"),
    meta: {
      requiresAuth: needsAuth,
    },
  },
  {
    path: "/about",
    name: "About",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/About.vue"),
  },
  {
    path: "/accept_invite",
    name: "InvitePage",
    component: () =>
      import(/* webpackChunkName: "invite" */ "../views/AcceptInvite.vue"),
  },
  {
    path: "/dashboard",
    component: DefaultLayout,
    children: [
      {
        path: "",
        name: "User Dashboard",
        component: () =>
          import(/* webpackChunkName: "dashboard" */ "../views/Dashboard.vue"),
        meta: {
          requiresAuth: needsAuth,
        },
      },
    ],
  },
  {
    path: "/create-firm",
    name: "Create Firm",
    component: () =>
      import(
        /* webpackChunkName: "firm" */ "../views/firm/firm-creation/NewFirm.vue"
      ),
    meta: {
      requiresAuth: needsAuth,
    },
  },
  {
    path: "/firm/:id",
    name: "Firm",
    component: DashboardLayout,
    children: [
      {
        path: "",
        name: "Dashboard",
        component: () =>
          import(
            /* webpackChunkName: "firm" */ "../views/firm/FirmDashboard.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "settings",
        name: "Firm Settings",
        component: () =>
          import(
            /* webpackChunkName: "firm" */ "../views/firm/FirmSettings.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "profile",
        name: "Profile",
        component: () =>
          import(/* webpackChunkName: "profile" */ "../views/Profile.vue"),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "clients",
        name: "Clients",
        component: () =>
          import(/* webpackChunkName: "firm" */ "../views/firm/AllClients.vue"),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "inventors",
        name: "Inventors",
        component: () =>
          import(
            /* webpackChunkName: "firm" */ "../views/firm/AllInventors.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "patents",
        name: "Patent Applications",
        component: () =>
          import(/* webpackChunkName: "firm" */ "../views/firm/Patents.vue"),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "patents/new",
        name: "New Patent Application",
        component: () =>
          import(
            /* webpackChunkName: "firm" */ "../views/firm/NewPatentPage.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "patents/:patentId/edit",
        name: "Edit Patent Application",
        component: () =>
          import(
            /* webpackChunkName: "firm" */ "../views/firm/NewPatentPage.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "patents/:patentId/:tab?",
        name: "Patent Application",
        component: () =>
          import(
            /* webpackChunkName: "firm" */ "../views/firm/IndividualPatent.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "members",
        name: "Members",
        component: () =>
          import(/* webpackChunkName: "firm" */ "../views/firm/Team.vue"),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "office-actions",
        name: "Office Actions",
        component: () =>
          import(
            /* webpackChunkName: "firm" */ "../views/firm/OfficeActions.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "documents/:documentId",
        name: "Document",
        hideTitle: true,
        component: () =>
          import(
            /* webpackChunkName: "firm" */ "../views/firm/DocumentReview.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      // {
      //   path: "notifications",
      //   name: "Notifications",
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "firm" */ "../views/firm/Notifications.vue"
      //     ),
      //   meta: {
      //     requiresAuth: needsAuth,
      //   },
      // },
      {
        path: "docketing",
        name: "Docketing",
        component: () =>
          import(/* webpackChunkName: "firm" */ "../views/firm/Docketing.vue"),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "litigation",
        name: "Litigation Cases",
        component: () =>
          import(
            /* webpackChunkName: "litigation" */ "../views/litigation/LitigationCases.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "litigation/new",
        name: "New Litigation Case",
        component: () =>
          import(
            /* webpackChunkName: "litigation" */ "../views/litigation/NewLitigationCase.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "litigation/:caseId/:tab?",
        name: "Litigation Case",
        component: () =>
          import(
            /* webpackChunkName: "litigation" */ "../views/litigation/LitigationCase.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        // set-level response-drafting workspace: source document (left) +
        // the whole response document in a rich-text editor (right) + chat rail
        path: "litigation/:caseId/discovery/:setId/response",
        name: "Discovery Response",
        component: () =>
          import(
            /* webpackChunkName: "litigation" */ "../views/litigation/DiscoveryResponseWorkspace.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "trademarks",
        name: "Trademarks",
        component: () =>
          import(/* webpackChunkName: "firm" */ "../views/firm/Trademarks.vue"),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "trademarks/:serialNumber/:tab?",
        name: "Trademark",
        component: () =>
          import(/* webpackChunkName: "firm" */ "../views/firm/IndividualTrademark.vue"),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "trademark-docketing",
        name: "Trademark Docketing",
        component: () =>
          import(
            /* webpackChunkName: "firm" */ "../views/firm/TrademarkDocketing.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "tm-document-review/:serialNumber/:docId?",
        name: "Trademark Document Review",
        component: () =>
          import(
            /* webpackChunkName: "firm" */ "../views/firm/TmDocumentReview.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "emails",
        name: "Emails",
        component: () =>
          import(/* webpackChunkName: "firm" */ "../views/firm/Emails.vue"),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "cloud-storage",
        name: "Cloud Storage",
        component: () =>
          import(
            /* webpackChunkName: "firm" */ "../views/firm/CloudStorage.vue"
          ),
        meta: {
          requiresAuth: needsAuth,
        },
      },
      {
        path: "help",
        name: "Help",
        component: () =>
          import(/* webpackChunkName: "help" */ "../views/firm/Help.vue"),
      },
    ],
  },
  {
    path: "/:catchAll(.*)*",
    component: () =>
      import(/* webpackChunkName: "help" */ "../views/NotFoundComponent.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

let firstRoute = false;

router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some((x) => x.meta.requiresAuth);
  if (requiresAuth && !auth.currentUser && firstRoute) {
    next("/signin");
    return;
  }
  if (!firstRoute) firstRoute = true;
  // litigation launch gate: bounce direct navigation for firms not on the
  // backend allowlist. Skipped while auth is still restoring (hard refresh —
  // no token yet); the sidebar stays hidden and the backend 403s regardless.
  if (auth.currentUser && to.params.id && to.path.includes("/litigation")) {
    if (!(await canUseLit(to.params.id))) {
      next(`/firm/${to.params.id}`);
      return;
    }
  }
  next();
});

export default router;
